import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getProjects, createProject, deleteProject, type Project } from '../lib/api';
import styles from './Dashboard.module.css';

export default function Dashboard() {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState('');
  
  const queryClient = useQueryClient();
  
  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
  });
  
  const createMutation = useMutation({
    mutationFn: () => createProject(newName, newGroup || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setShowCreate(false);
      setNewName('');
      setNewGroup('');
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
  
  // Group projects by groupName
  const groupedProjects = projects?.reduce((acc, project) => {
    const group = project.groupName || 'Ungrouped';
    if (!acc[group]) acc[group] = [];
    acc[group].push(project);
    return acc;
  }, {} as Record<string, Project[]>) || {};
  
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <span>Loading projects...</span>
      </div>
    );
  }
  
  return (
    <div className={styles.dashboard}>
      <header className={styles.header}>
        <div>
          <h1>Projects</h1>
          <p className={styles.subtitle}>
            {projects?.length || 0} project{projects?.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreate(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          New Project
        </button>
      </header>
      
      {showCreate && (
        <div className={styles.createForm}>
          <h3>Create New Project</h3>
          <div className={styles.formFields}>
            <input
              type="text"
              className="input"
              placeholder="Project name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
            <input
              type="text"
              className="input"
              placeholder="Group (optional)"
              value={newGroup}
              onChange={(e) => setNewGroup(e.target.value)}
            />
          </div>
          <div className={styles.formActions}>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => createMutation.mutate()}
              disabled={!newName || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}
      
      {Object.keys(groupedProjects).length === 0 ? (
        <div className={styles.empty}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 3h18v18H3z"/>
            <path d="M12 8v8m-4-4h8"/>
          </svg>
          <h3>No projects yet</h3>
          <p>Create your first project to get started</p>
        </div>
      ) : (
        <div className={styles.groups}>
          {Object.entries(groupedProjects).map(([group, projectList]) => (
            <div key={group} className={styles.group}>
              <h2 className={styles.groupName}>{group}</h2>
              <div className={styles.projectGrid}>
                {projectList.map((project) => (
                  <Link
                    key={project.projectId}
                    to={`/projects/${project.projectId}`}
                    className={styles.projectCard}
                  >
                    <div className={styles.projectIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>
                        <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>
                      </svg>
                    </div>
                    <div className={styles.projectInfo}>
                      <h3>{project.name}</h3>
                      <span className={styles.projectDate}>
                        Created {new Date(project.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <button
                      className={styles.deleteBtn}
                      onClick={(e) => {
                        e.preventDefault();
                        if (confirm('Delete this project?')) {
                          deleteMutation.mutate(project.projectId);
                        }
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

